import { useEffect, useRef } from 'react';
import { isMac } from '@/lib/utils';
import { pushModal, popModal, isTopmostModal } from '@/lib/modalStack';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  handler: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
}

/**
 * Platform-aware keyboard shortcut hook
 * Automatically handles Mac (Cmd) vs Windows/Linux (Ctrl) differences
 *
 * Uses a ref to always access the latest shortcuts without re-registering
 * the event listener on every render.
 */
export function useKeyboard(shortcuts: KeyboardShortcut[]) {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMacOS = isMac();
      const target = event.target as HTMLElement;

      // Check if target is an editable element
      const isEditableElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      for (const shortcut of shortcutsRef.current) {
        const {
          key,
          ctrlKey = false,
          shiftKey = false,
          altKey = false,
          metaKey = false,
          handler,
          preventDefault = true,
        } = shortcut;

        // Check key match (case-insensitive)
        if (event.key.toLowerCase() !== key.toLowerCase()) {
          continue;
        }

        // Platform-aware modifier key check
        const modifierKey = isMacOS ? metaKey : ctrlKey;
        const eventModifierKey = isMacOS ? event.metaKey : event.ctrlKey;

        // Check all modifier keys
        if (
          eventModifierKey === modifierKey &&
          event.shiftKey === shiftKey &&
          event.altKey === altKey
        ) {
          // Skip editing shortcuts (paste, copy, cut, undo, redo, select all, delete, backspace)
          // when target is an editable element to allow native browser behavior
          const isEditingShortcut = ['v', 'c', 'x', 'z', 'y', 'a'].includes(key.toLowerCase());
          const isDeleteKey = ['delete', 'backspace'].includes(key.toLowerCase());
          if (isEditableElement && (isEditingShortcut || isDeleteKey)) {
            continue; // Skip this shortcut, allow browser's native behavior
          }

          // Allow native copy/cut when user has selected text outside editable elements
          // (e.g., selecting a heading or label text and pressing Cmd+C)
          if (['c', 'x'].includes(key.toLowerCase()) && eventModifierKey) {
            const selection = window.getSelection();
            if (selection && selection.toString().trim().length > 0) {
              continue; // Allow native browser copy/cut
            }
          }

          if (preventDefault) {
            event.preventDefault();
          }
          handler(event);
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Register once, always reads latest shortcuts from ref
}

/**
 * Cmd+Enter (Mac) / Ctrl+Enter (Windows/Linux) "save" shortcut for modals that
 * render their own overlay instead of using the shared <Dialog> component.
 *
 * The listener is only attached while `enabled` is true (pass the modal's `open`
 * state), so a closed modal never reacts to the shortcut. Presses made during an
 * IME composition are ignored so confirming Korean text does not submit.
 *
 * While enabled, the modal registers in the shared modal stack (see
 * `@/lib/modalStack`) and only fires when it is the topmost modal. This prevents
 * a self-overlay parent and its nested child from both submitting on a single
 * Cmd/Ctrl+Enter, and keeps ordering correct when mixed with <Dialog> modals.
 */
export function useSubmitShortcut(
  onSubmit: (() => void) | undefined,
  enabled = true,
) {
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const idRef = useRef<symbol>(Symbol('submit-shortcut'));

  useEffect(() => {
    if (!enabled) return undefined;

    const id = idRef.current;
    pushModal(id);

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only the frontmost modal reacts to the shortcut
      if (!isTopmostModal(id)) return;

      if (
        event.key === 'Enter' &&
        (event.metaKey || event.ctrlKey) &&
        !event.isComposing &&
        onSubmitRef.current
      ) {
        event.preventDefault();
        event.stopPropagation();
        onSubmitRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      popModal(id);
    };
  }, [enabled]);
}

/**
 * Common keyboard shortcuts for spreadsheet operations
 */
export function useSpreadsheetKeyboard({
  onSave,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onSelectAll,
  onDelete,
  onEscape,
}: {
  onSave?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onSelectAll?: () => void;
  onDelete?: () => void;
  onEscape?: () => void;
}) {
  const shortcuts: KeyboardShortcut[] = [
    // Save: Ctrl+S / Cmd+S
    {
      key: 's',
      ctrlKey: true,
      metaKey: true,
      handler: () => onSave?.(),
    },
    // Undo: Ctrl+Z / Cmd+Z
    {
      key: 'z',
      ctrlKey: true,
      metaKey: true,
      handler: () => onUndo?.(),
    },
    // Redo: Ctrl+Y / Cmd+Shift+Z
    {
      key: 'y',
      ctrlKey: true,
      handler: () => onRedo?.(),
    },
    {
      key: 'z',
      ctrlKey: true,
      metaKey: true,
      shiftKey: true,
      handler: () => onRedo?.(),
    },
    // Copy: Ctrl+C / Cmd+C
    {
      key: 'c',
      ctrlKey: true,
      metaKey: true,
      handler: () => onCopy?.(),
    },
    // Paste: Ctrl+V / Cmd+V
    {
      key: 'v',
      ctrlKey: true,
      metaKey: true,
      handler: () => onPaste?.(),
    },
    // Select All: Ctrl+A / Cmd+A
    {
      key: 'a',
      ctrlKey: true,
      metaKey: true,
      handler: () => onSelectAll?.(),
    },
    // Delete: Delete key
    {
      key: 'Delete',
      handler: () => onDelete?.(),
    },
    // Escape: Escape key
    {
      key: 'Escape',
      handler: () => onEscape?.(),
      preventDefault: false, // Allow default behavior
    },
  ];

  useKeyboard(shortcuts);
}
